/**
 *  <la-fit-check>
 *  ---------------
 *  Checks what the customer reads off the inside of their temple arm -- the
 *  model number, the lens width, or the whole marking ("RB2140 901 50□22") --
 *  against this listing.
 *
 *  - Models come from the product title (RW4006, RB2140F, OO9102, ...) plus an
 *    optional `custom.fits_models` metafield, passed in as data attributes.
 *  - Sizes come from the Size option, or from the title ("(57 mm)") when a
 *    model's sizes are sold as separate listings.
 *  - The answer is checked against the size the customer has SELECTED. A
 *    mismatch offers a one-click switch rather than changing it silently, and
 *    a warning then follows the size picker for the rest of the visit.
 *
 *  parseInput / parseModels / evaluate are pure, so they can be unit-tested
 *  without a DOM.
 */
if (!customElements.get('la-fit-check')) {
  const PREFIXES = 'RB|RW|OW|OO|OX|RX|OJ';
  const WIDTH_MIN = 38;
  const WIDTH_MAX = 70;

  class LaFitCheck extends HTMLElement {
    /* ================= pure helpers ================= */

    /** "RW4013F (GEN2)" -> [{ prefix:'RW', digits:'4013', suffix:'F', label:'RW4013F' }] */
    static parseModels(text) {
      const out = [];
      const re = new RegExp(`(^|[^A-Z0-9])(${PREFIXES})\\s*-?\\s*(\\d{4})([A-Z])?(?![A-Z0-9])`, 'g');
      const s = String(text || '').toUpperCase();
      let m;
      while ((m = re.exec(s))) {
        const model = { prefix: m[2], digits: m[3], suffix: m[4] || '' };
        model.label = model.prefix + model.digits + model.suffix;
        if (!out.some((x) => x.label === model.label)) out.push(model);
      }
      return out;
    }

    /** Lens widths from the Size option ("50mm,54mm") or a title ("(57 mm)"). */
    static parseSizes(csv, title) {
      const fromCsv = String(csv || '')
        .split(',')
        .map((v) => parseInt((v.match(/\d{2}/) || [])[0], 10))
        .filter((n) => n >= WIDTH_MIN && n <= WIDTH_MAX);
      if (fromCsv.length) return { sizes: [...new Set(fromCsv)], fromTitle: false };
      const t = String(title || '').match(/(^|[^\d])(\d{2})\s?mm\b/i);
      const n = t ? parseInt(t[2], 10) : NaN;
      return n >= WIDTH_MIN && n <= WIDTH_MAX ? { sizes: [n], fromTitle: true } : { sizes: [], fromTitle: false };
    }

    /**
     * Whatever the customer typed -> { model, partial, size }.
     *   "RB2140 901 50□22 3N" -> model RB2140, size 50
     *   "rw40"                -> partial
     *   "2140F"               -> model digits 2140, suffix F, no prefix
     */
    static parseInput(raw) {
      let s = String(raw || '').toUpperCase().replace(/[’']/g, '');
      let model = null;
      let partial = false;

      const withPrefix = s.match(new RegExp(`(^|[^A-Z0-9])(${PREFIXES})\\s*-?\\s*(\\d{1,4})([A-Z])?(?![A-Z0-9])`));
      if (withPrefix) {
        if (withPrefix[3].length < 4) partial = true;
        else model = { prefix: withPrefix[2], digits: withPrefix[3], suffix: withPrefix[4] || '' };
        s = s.replace(withPrefix[0], ' ');
      } else {
        const bare = s.match(/(^|[^\d/.])(\d{4})([A-Z])?(?![A-Z0-9])/);
        if (bare) {
          model = { prefix: '', digits: bare[2], suffix: bare[3] || '' };
          s = s.replace(bare[0], ' ');
        }
      }
      if (model) model.label = model.prefix + model.digits + model.suffix;

      let size = null;
      const re = /(^|[^\d/.])(\d{2})(?!\d)/g;
      let m;
      while ((m = re.exec(s))) {
        const n = parseInt(m[2], 10);
        if (n >= WIDTH_MIN && n <= WIDTH_MAX) { size = n; break; }
      }
      return { model, partial, size };
    }

    /** "exact" | "near" (same model, different letter) | "none" */
    static compareModel(yours, theirs) {
      if (yours.digits !== theirs.digits) return 'none';
      if (yours.prefix && yours.prefix !== theirs.prefix) return 'none';
      return yours.suffix === theirs.suffix ? 'exact' : 'near';
    }

    static list(sizes) {
      const l = sizes.map((n) => `${n}mm`);
      return l.length <= 1 ? l.join('') : `${l.slice(0, -1).join(', ')} or ${l[l.length - 1]}`;
    }

    /**
     * ctx: { models, sizes, fromTitle, hasSizeOption, selected, msg(key, vars), searchUrl }
     * -> { tone: 'yes'|'warn'|'no', lines: [..], action: null | {type, ...}, frameSize }
     */
    static evaluate(raw, ctx) {
      const p = LaFitCheck.parseInput(raw);
      const say = ctx.msg;
      const sizes = ctx.sizes;
      const search = (q) => `${ctx.searchUrl}?q=${encodeURIComponent(q)}&type=product`;

      if (!p.model && !p.size) {
        return { tone: 'no', lines: [say(p.partial ? 'partial' : 'invalid', {})], action: null };
      }

      const lines = [];
      let tone = 'yes';
      let action = null;
      let matched = ctx.models[0] || null;

      // ---- model ----
      if (p.model) {
        if (!ctx.models.length) {
          lines.push(say('modelUnknown', { yours: p.model.label }));
          tone = 'warn';
        } else {
          const ranked = ctx.models.map((m) => ({ m, r: LaFitCheck.compareModel(p.model, m) }));
          const exact = ranked.find((x) => x.r === 'exact');
          const near = ranked.find((x) => x.r === 'near');
          if (exact) {
            matched = exact.m;
            lines.push(say('modelYes', { model: matched.label }));
          } else {
            const target = (near || ranked[0]).m;
            // Ray-Ban's F suffix is the Low-Bridge Fit version (the store's own
            // model-check.js encodes the same rule), so say so plainly.
            let key = near ? 'modelNear' : 'modelNo';
            if (near && /^R[BW]$/.test(target.prefix)) {
              if (target.suffix === 'F' && !p.model.suffix) key = 'nearToLbf';
              else if (p.model.suffix === 'F' && !target.suffix) key = 'nearFromLbf';
            }
            return {
              tone: 'no',
              lines: [say(key, { model: target.label, yours: p.model.label })],
              action: { type: 'link', href: search(p.model.label), label: say('findLabel', { yours: p.model.label }) },
            };
          }
        }
      }

      // ---- size ----
      const vars = { size: p.size, sizes: LaFitCheck.list(sizes), selected: ctx.selected, model: matched ? matched.label : '' };
      if (p.size) {
        if (!sizes.length) {
          if (!p.model) {
            return { tone: 'no', lines: [say(ctx.models.length ? 'needModel' : 'invalid', vars)], action: null };
          }
        } else if (sizes.includes(p.size)) {
          if (ctx.hasSizeOption) {
            if (ctx.selected === p.size) {
              lines.push(say('sizeSelected', vars));
            } else {
              tone = 'warn';
              lines.push(say('sizeSwitch', vars));
              action = { type: 'switch', size: p.size, label: say('switchLabel', vars) };
            }
          } else {
            lines.push(say('sizeListing', vars));
          }
          return { tone, lines, action, frameSize: ctx.hasSizeOption ? p.size : null };
        } else {
          tone = 'no';
          if (ctx.hasSizeOption) {
            lines.push(say('sizeNo', vars));
          } else {
            lines.push(say('sizeOtherListing', vars));
            if (matched) action = { type: 'link', href: search(matched.label), label: say('seeSizesLabel', vars) };
          }
        }
      } else if (sizes.length > 1 && ctx.hasSizeOption && tone === 'yes') {
        lines.push(say('needSize', vars));
      }
      return { tone, lines, action };
    }

    /* ================= element ================= */

    connectedCallback() {
      this.form = this.querySelector('.la-fit__form');
      this.input = this.querySelector('.la-fit__input');
      this.result = this.querySelector('.la-fit__result');
      if (!this.form || !this.input || !this.result) return;

      const d = this.dataset;
      let extra = [];
      try {
        const parsed = JSON.parse(d.extraModels || 'null');
        extra = Array.isArray(parsed) ? parsed : String(parsed || '').split(',');
      } catch (e) {
        extra = String(d.extraModels || '').split(',');
      }
      this.models = LaFitCheck.parseModels(`${d.title || ''} ${extra.join(' ')}`);
      const s = LaFitCheck.parseSizes(d.sizes, d.title);
      this.sizes = s.sizes;
      this.fromTitle = s.fromTitle;
      this.storeKey = `la-fit:${d.productId || ''}`;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.check();
      });
      this.input.addEventListener('input', () => this.clear());

      this.onPickerChange = (event) => {
        if (event.target && event.target.closest && event.target.closest(this.fieldsetSelector())) this.updateWarning();
      };
      document.addEventListener('change', this.onPickerChange);

      try { this.frameSize = parseInt(sessionStorage.getItem(this.storeKey), 10) || null; } catch (e) { this.frameSize = null; }
      this.updateWarning();
    }

    disconnectedCallback() {
      if (this.onPickerChange) document.removeEventListener('change', this.onPickerChange);
    }

    msg(key, vars) {
      const attr = `msg${key.charAt(0).toUpperCase()}${key.slice(1)}`;
      return (this.dataset[attr] || '').replace(/\[(\w+)\]/g, (all, name) =>
        vars[name] === undefined || vars[name] === null ? '' : String(vars[name])
      );
    }

    /* ---------- the variant picker ---------- */

    fieldsetSelector() {
      const handle = (this.dataset.sizeOption || '').toLowerCase().replace(/ /g, '-');
      return handle ? `.variations .product-form__input[data-handle="${handle}"]` : null;
    }

    fieldset() {
      const sel = this.fieldsetSelector();
      return sel ? document.querySelector(sel) : null;
    }

    selectedSize() {
      const fs = this.fieldset();
      const checked = fs && fs.querySelector('input[type="radio"]:checked');
      const n = checked ? parseInt((checked.value.match(/\d{2}/) || [])[0], 10) : NaN;
      return Number.isNaN(n) ? null : n;
    }

    selectSize(size) {
      const fs = this.fieldset();
      if (!fs) return;
      for (const radio of fs.querySelectorAll('input[type="radio"]')) {
        const n = parseInt((radio.value.match(/\d{2}/) || [])[0], 10);
        if (n !== size || radio.classList.contains('is-disabled')) continue;
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    }

    /** A warning under the size picker whenever it disagrees with the checked frame. */
    updateWarning() {
      const fs = this.fieldset();
      if (!fs) return;
      let warn = fs.parentElement.querySelector('.la-fit-warn');
      const selected = this.selectedSize();
      const wrong = this.frameSize && this.sizes.includes(this.frameSize) && selected && selected !== this.frameSize;

      if (!wrong) {
        if (warn) warn.hidden = true;
        return;
      }
      if (!warn) {
        warn = document.createElement('div');
        warn.className = 'la-fit-warn';
        warn.setAttribute('role', 'status');
        warn.innerHTML = '<span class="la-fit-warn__txt"></span><button type="button" class="la-fit-warn__btn"></button>';
        warn.querySelector('button').addEventListener('click', () => this.selectSize(this.frameSize));
        fs.insertAdjacentElement('afterend', warn);
      }
      const vars = { size: this.frameSize, selected };
      warn.querySelector('.la-fit-warn__txt').textContent = this.msg('pickerWarning', vars);
      warn.querySelector('.la-fit-warn__btn').textContent = this.msg('switchLabel', vars);
      warn.hidden = false;
    }

    /* ---------- the modal ---------- */

    clear() {
      this.result.hidden = true;
      this.result.replaceChildren();
      this.result.className = 'la-fit__result';
    }

    check() {
      const verdict = LaFitCheck.evaluate(this.input.value, {
        models: this.models,
        sizes: this.sizes,
        fromTitle: this.fromTitle,
        hasSizeOption: !!this.fieldset(),
        selected: this.selectedSize(),
        msg: (k, v) => this.msg(k, v),
        searchUrl: this.dataset.searchUrl || '/search',
      });

      if (verdict.frameSize) {
        this.frameSize = verdict.frameSize;
        try { sessionStorage.setItem(this.storeKey, String(verdict.frameSize)); } catch (e) { /* private mode */ }
        this.updateWarning();
      }
      this.render(verdict);
    }

    render(verdict) {
      this.clear();
      this.result.classList.add(`la-fit__result--${verdict.tone}`);
      verdict.lines.filter(Boolean).forEach((line) => {
        const p = document.createElement('p');
        p.textContent = line;
        this.result.appendChild(p);
      });

      const a = verdict.action;
      if (a && a.type === 'switch') {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'la-fit__action';
        btn.textContent = a.label;
        btn.addEventListener('click', () => {
          this.selectSize(a.size);
          this.render({ tone: 'yes', lines: [this.msg('sizeSelected', { size: a.size })], action: null });
        });
        this.result.appendChild(btn);
      } else if (a && a.type === 'link') {
        const link = document.createElement('a');
        link.className = 'la-fit__action la-fit__action--link';
        link.href = a.href;
        link.textContent = a.label;
        this.result.appendChild(link);
      }
      this.result.hidden = false;
    }
  }

  customElements.define('la-fit-check', LaFitCheck);
}
